/**
 * 공통 폼 클래스 – submit via fetch, validate, loader, disabled
 * CSRF: 폼에 _token hidden이 있으면 FormData에 포함됨. 필요 시 X-CSRF-TOKEN 메타에서 읽어 헤더 추가 가능.
 *
 * Accept: application/json + X-Requested-With: XMLHttpRequest 전송으로 서버에서
 * $request->expectsJson() 이 true 가 됨. 따라서 컨트롤러에서 expectsJson() 기준으로
 * JSON 응답(Response::json) 또는 리다이렉트/에러 페이지로 분기 가능.
 * 예외 발생 시 ExceptionHandler 도 wantsJson() 으로 JSON 에러 응답 반환.
 */

import { Logger } from "../utils/Logger";
import { Modal } from "../utils/Modal";

export interface FormOptions {
  /** POST URL (없으면 form.action 또는 data-submit-url 사용) */
  submitUrl?: string;
  /** 클라이언트 검증: 오류 메시지 반환 또는 null (async 지원) */
  validate?: (form: HTMLFormElement) => string | null | Promise<string | null>;
  /** 제출 버튼 선택자 (기본 'button[type="submit"]') */
  submitButtonSelector?: string;
  /** 로더 숨김/표시할 요소 선택자 (버튼 내부) */
  loaderSelector?: string;
  /** 제출 중 버튼 라벨로 잠시 교체할 텍스트(예: "제출 중..."). 종료 시 원본 복원. */
  submittingText?: string;
  /** 성공 시 (응답 JSON, 파싱된 data) */
  onSuccess?: (data: Record<string, unknown>) => void | Promise<void>;
  /** 실패 시 (메시지, 응답 data) */
  onFail?: (message: string, data?: Record<string, unknown>) => void | Promise<void>;
  /** 요청 실패(네트워크 등) 시 기본 메시지 */
  requestFailedMessage?: string;
  /** JSON 파싱 실패 시 기본 메시지 */
  invalidResponseMessage?: string;
}

const DEFAULT_SUBMIT_SELECTOR = 'button[type="submit"]';

export class Form {
  private readonly form: HTMLFormElement;
  private readonly opts: Required<Pick<FormOptions, "submitButtonSelector" | "loaderSelector">> &
    FormOptions;
  private originalButtonHtml: string | null = null;

  constructor(form: HTMLFormElement, options: FormOptions = {}) {
    this.form = form;
    this.opts = {
      submitButtonSelector: options.submitButtonSelector ?? DEFAULT_SUBMIT_SELECTOR,
      loaderSelector: options.loaderSelector ?? "",
      ...options,
    };
  }

  init(): void {
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
  }

  private getSubmitUrl(): string | null {
    return (
      this.opts.submitUrl ??
      (this.form.action || this.form.dataset.submitUrl || null)
    );
  }

  private getSubmitButton(): HTMLButtonElement | null {
    const sel = this.opts.submitButtonSelector;
    const internal = this.form.querySelector<HTMLButtonElement>(sel);
    if (internal) return internal;
    // form="id" 로 폼 외부에 선언된 submit 버튼도 탐색 (nested form 방지 패턴)
    const formId = this.form.id;
    if (formId) {
      return document.querySelector<HTMLButtonElement>(`${sel}[form="${formId}"]`);
    }
    return null;
  }

  private getLoader(): HTMLElement | null {
    const btn = this.getSubmitButton();
    if (!btn || !this.opts.loaderSelector) return null;
    return btn.querySelector<HTMLElement>(this.opts.loaderSelector);
  }

  setSubmitting(submitting: boolean): void {
    const btn = this.getSubmitButton();
    if (!btn) return;
    btn.disabled = submitting;
    const loader = this.getLoader();
    if (loader) {
      loader.style.display = submitting ? "inline-block" : "none";
    }
    // 버튼 라벨을 "제출 중..." 텍스트로 잠시 교체. loaderSelector 가 있으면 텍스트만 교체해도 로더가 함께 보임.
    if (this.opts.submittingText) {
      if (submitting) {
        if (this.originalButtonHtml === null) {
          this.originalButtonHtml = btn.innerHTML;
        }
        // 텍스트 노드만 교체 — 자식 요소(로더 span 등)는 그대로 유지
        for (const node of Array.from(btn.childNodes)) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
            node.textContent = this.opts.submittingText + " ";
            break;
          }
        }
      } else if (this.originalButtonHtml !== null) {
        btn.innerHTML = this.originalButtonHtml;
        this.originalButtonHtml = null;
      }
    }
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    if (this.opts.validate) {
      const errorMsg = await Promise.resolve(this.opts.validate(this.form));
      if (errorMsg != null) {
        if (this.opts.onFail) this.opts.onFail(errorMsg);
        else await Modal.alert(errorMsg);
        return;
      }
    }

    const url = this.getSubmitUrl();
    if (!url) {
      Logger.warn("Form: no submit URL");
      return;
    }

    this.setSubmitting(true);

    try {
      const body = new FormData(this.form);
      // HTTP 메서드는 항상 POST로 유지. _method 필드를 body에 남겨서
      // 서버 Request::method()의 스푸핑 로직이 PATCH/PUT/DELETE로 처리하게 한다.
      // PHP는 multipart/form-data를 POST에서만 자동 파싱하므로 실제 PATCH/PUT/DELETE
      // 전송 시 $_POST가 비어 CSRF 토큰을 읽지 못한다.
      const res = await fetch(url, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const raw = await res.text();
      let data: Record<string, unknown> = {};
      try {
        data = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        const msg =
          this.opts.invalidResponseMessage ?? "Invalid response.";
        if (this.opts.onFail) this.opts.onFail(msg);
        else await Modal.alert(msg);
        return;
      }

      const result = data?.result ?? data?.success;
      const msg = (data?.msg ?? data?.message ?? "") as string;

      if (result === "success" || result === true) {
        if (this.opts.onSuccess) await this.opts.onSuccess(data);
        else await Modal.alert(msg || "Success.");
      } else {
        if (this.opts.onFail) await this.opts.onFail(msg || "Error.", data);
        else await Modal.alert(msg || "Error.");
      }
    } catch {
      const msg = this.opts.requestFailedMessage ?? "Request failed.";
      if (this.opts.onFail) await this.opts.onFail(msg);
      else await Modal.alert(msg);
      Logger.error("Form submit failed", url);
    } finally {
      this.setSubmitting(false);
    }
  }
}
